/**
 * Modal, drawer, dropdown, popover, tooltip, confirm.
 *
 * All of them share three behaviours that are easy to get wrong individually
 * and are therefore implemented once here:
 *
 * - **Escape closes, and a click outside closes.** `pointerdown`, not `click`:
 *   a menu that waits for click-up stays open through a drag that began outside
 *   it, which reads as stuck.
 * - **Focus is put inside on open and returned on close.** Without the return,
 *   closing a dialog drops the keyboard user back at the top of the document.
 * - **They render in a portal**, so an overflow-hidden ancestor cannot clip
 *   them — the single most common cause of a menu that opens invisibly inside a
 *   scrolling table.
 */

import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
  type RefObject,
} from 'react';
import { createPortal } from 'react-dom';
import { Check, X } from 'lucide-react';

import { cn } from '../../lib/cn';
import { DIALOG_BODY, useDialogBody, useDismiss } from '../../lib/hooks';
import { stepContent, useWizard, type WizardStep } from '../../lib/wizard';
import { Button, type ButtonVariant } from './button';
import { ScrollArea } from './scroll-area';

/** Renders into `document.body`, so nothing upstream can clip or stack over it. */
function Portal({ children }: { children: ReactNode }) {
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  if (!mounted) return null;
  return createPortal(children, document.body);
}

/**
 * Trap focus inside a container while it is open, and give it back on close.
 *
 * Tab and Shift+Tab cycle within the dialog. Without this a keyboard user tabs
 * straight out of an open modal and into the page behind it, where they can
 * operate controls the modal is covering.
 */
function useFocusTrap(ref: RefObject<HTMLElement | null>, active: boolean): void {
  useEffect(() => {
    if (!active) return;
    const container = ref.current;
    if (!container) return;

    const previous = document.activeElement as HTMLElement | null;

    const focusable = () =>
      Array.from(
        container.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      ).filter((element) => element.offsetParent !== null);

    // The first control, or the container itself when there is none — a dialog
    // that is only text still has to receive focus so Escape reaches it.
    const initial = focusable()[0] ?? container;
    initial.focus({ preventScroll: true });

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') return;
      const items = focusable();
      if (items.length === 0) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (!first || !last) return;

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    container.addEventListener('keydown', onKeyDown);
    return () => {
      container.removeEventListener('keydown', onKeyDown);
      previous?.focus({ preventScroll: true });
    };
  }, [ref, active]);
}

/** Stop the page behind an overlay from scrolling. */
function useScrollLock(active: boolean): void {
  useEffect(() => {
    if (!active) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [active]);
}

// ── modal ───────────────────────────────────────────────────
export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  description?: ReactNode;
  /** The action row. Rendered in a footer that stays put while the body scrolls. */
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  children?: ReactNode;
  /**
   * Whether a backdrop click closes it.
   *
   * Off for a dialog holding unsaved input: losing a half-filled form to a
   * stray click is the kind of small betrayal people remember.
   */
  dismissOnBackdrop?: boolean;
}

export function Modal({
  open,
  onClose,
  title,
  description,
  footer,
  size = 'md',
  children,
  dismissOnBackdrop = true,
}: ModalProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();

  useFocusTrap(panelRef, open);
  useScrollLock(open);
  useDialogBody(panelRef, open);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  const widths = { sm: 'max-w-sm', md: 'max-w-lg', lg: 'max-w-2xl', xl: 'max-w-4xl' } as const;

  return (
    <Portal>
      <div className="fixed inset-0 z-overlay flex items-center justify-center p-4">
        <div
          className="absolute inset-0 animate-fade-in bg-black/40 backdrop-blur-[1px]"
          onClick={dismissOnBackdrop ? onClose : undefined}
          aria-hidden
        />
        <div
          ref={panelRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby={title ? titleId : undefined}
          tabIndex={-1}
          className={cn(
            'relative flex max-h-[90vh] w-full animate-scale-in flex-col overflow-hidden rounded-card bg-surface shadow-modal',
            widths[size],
          )}
        >
          {title ? (
            <div className="flex items-start justify-between gap-4 border-b border-line px-5 py-3.5">
              <div className="space-y-0.5">
                <h2 id={titleId} className="text-sm font-semibold text-heading">
                  {title}
                </h2>
                {description ? <p className="text-xs text-muted">{description}</p> : null}
              </div>
              <button
                type="button"
                onClick={onClose}
                className="-m-1 rounded p-1 text-faint transition-colors hover:bg-surface-2 hover:text-body"
                aria-label="Close"
              >
                <X className="size-4" />
              </button>
            </div>
          ) : null}

          <ScrollArea className={DIALOG_BODY} contentClassName="px-5 py-4">{children}</ScrollArea>

          {footer ? (
            <div className="flex items-center justify-end gap-2 border-t border-line bg-surface-2/50 px-5 py-3">
              {footer}
            </div>
          ) : null}
        </div>
      </div>
    </Portal>
  );
}

// ── drawer ──────────────────────────────────────────────────
/**
 * A side panel.
 *
 * Used instead of a modal where the record behind it is still worth seeing — a
 * filter panel, a detail peek from a register. A modal for those hides the list
 * the user is working through.
 */
export function Drawer({
  open,
  onClose,
  title,
  footer,
  width = 'md',
  children,
}: {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  footer?: ReactNode;
  width?: 'sm' | 'md' | 'lg';
  children?: ReactNode;
}) {
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();

  useFocusTrap(panelRef, open);
  useScrollLock(open);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  const widths = { sm: 'max-w-sm', md: 'max-w-md', lg: 'max-w-xl' } as const;

  return (
    <Portal>
      <div className="fixed inset-0 z-overlay flex justify-end">
        <div className="absolute inset-0 animate-fade-in bg-black/40" onClick={onClose} aria-hidden />
        <div
          ref={panelRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby={title ? titleId : undefined}
          tabIndex={-1}
          className={cn(
            'relative flex h-full w-full animate-slide-in-right flex-col bg-surface shadow-modal',
            widths[width],
          )}
        >
          {title ? (
            <div className="flex items-center justify-between gap-4 border-b border-line px-5 py-3.5">
              <h2 id={titleId} className="text-sm font-semibold text-heading">
                {title}
              </h2>
              <button
                type="button"
                onClick={onClose}
                className="-m-1 rounded p-1 text-faint hover:bg-surface-2 hover:text-body"
                aria-label="Close"
              >
                <X className="size-4" />
              </button>
            </div>
          ) : null}
          <ScrollArea className="min-h-0 flex-1" contentClassName="px-5 py-4">{children}</ScrollArea>
          {footer ? (
            <div className="flex items-center justify-end gap-2 border-t border-line px-5 py-3">
              {footer}
            </div>
          ) : null}
        </div>
      </div>
    </Portal>
  );
}

// ── popover ─────────────────────────────────────────────────
/**
 * An anchored panel.
 *
 * Positioned in a portal from the trigger's measured rectangle, and **flipped
 * when it would leave the viewport**. A column filter opened on the last column
 * of a wide table is exactly where an unflipped popover renders off-screen.
 */
export function Popover({
  open,
  onClose,
  anchorRef,
  align = 'start',
  className,
  children,
  label,
}: {
  open: boolean;
  onClose: () => void;
  anchorRef: RefObject<HTMLElement | null>;
  align?: 'start' | 'end' | 'center';
  className?: string;
  children: ReactNode;
  label?: string;
}) {
  const panelRef = useRef<HTMLDivElement | null>(null);
  // The panel element as state as well as a ref. The portal mounts it a render
  // after `open` turns true, so a placement run on opening finds nothing to
  // measure — and a ref changing re-runs nothing, which left the panel hidden
  // at 0,0 until the page happened to scroll. State re-runs the placement the
  // moment the panel exists.
  const [panel, setPanel] = useState<HTMLDivElement | null>(null);
  const attachPanel = useCallback((node: HTMLDivElement | null) => {
    panelRef.current = node;
    setPanel(node);
  }, []);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);
  // Published as a custom property so a panel can choose to match its
  // trigger's width — which is what a picker wants and a menu does not.
  const [anchorWidth, setAnchorWidth] = useState<number | null>(null);

  useDismiss(panelRef, onClose, open);

  const place = useCallback(() => {
    const anchor = anchorRef.current;
    const panel = panelRef.current;
    if (!anchor || !panel) return;

    const rect = anchor.getBoundingClientRect();
    // Its laid-out size, not its rectangle: it is measured while it is still
    // arriving (`animate-scale-in`), scaled down, and a rectangle would call
    // it smaller than it is — which leaves a panel aligned to its trigger's
    // right edge hanging past it, and one at the screen's edge partly off it.
    const panelRect = { width: panel.offsetWidth, height: panel.offsetHeight };
    const margin = 8;

    let left =
      align === 'end'
        ? rect.right - panelRect.width
        : align === 'center'
          ? rect.left + rect.width / 2 - panelRect.width / 2
          : rect.left;
    // Clamped into the viewport rather than allowed to hang off the edge.
    left = Math.max(margin, Math.min(left, window.innerWidth - panelRect.width - margin));

    // Below by default; above when there is no room below but there is above.
    const below = rect.bottom + margin;
    const fitsBelow = below + panelRect.height <= window.innerHeight - margin;
    const top = fitsBelow ? below : Math.max(margin, rect.top - panelRect.height - margin);

    setPosition({ top, left });
    setAnchorWidth(rect.width);
  }, [anchorRef, align]);

  useEffect(() => {
    if (!open) {
      setPosition(null);
      return;
    }
    if (!panel) return;
    place();
    // Re-place on scroll and resize: the anchor is inside a scrolling table, so
    // a panel pinned at its opening coordinates drifts away from its trigger.
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open, panel, place]);

  if (!open) return null;

  return (
    <Portal>
      <div
        ref={attachPanel}
        role="dialog"
        aria-label={label}
        className={cn(
          'fixed z-popover animate-scale-in rounded-card border border-line bg-surface-popup shadow-dropdown',
          // Hidden until measured, so it never flashes at the wrong coordinates.
          position ? 'visible opacity-100' : 'invisible opacity-0',
          className,
        )}
        style={
          {
            ...(position ?? { top: 0, left: 0 }),
            '--anchor-width': anchorWidth ? `${anchorWidth}px` : undefined,
          } as CSSProperties
        }
      >
        {children}
      </div>
    </Portal>
  );
}

// ── dropdown menu ───────────────────────────────────────────
export interface MenuItem {
  label: ReactNode;
  onSelect?: () => void;
  icon?: ReactNode;
  /** Renders in the danger tone — for delete and the like. */
  destructive?: boolean;
  disabled?: boolean;
  /** A divider above this item. */
  separated?: boolean;
}

export function DropdownMenu({
  trigger,
  items,
  align = 'end',
  label = 'Actions',
  header,
}: {
  trigger: (props: { ref: RefObject<HTMLButtonElement | null>; onClick: () => void; 'aria-expanded': boolean }) => ReactNode;
  items: MenuItem[];
  align?: 'start' | 'end';
  label?: string;
  /** Shown above the items and not selectable — who an account menu belongs to. */
  header?: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);

  // An empty menu renders no trigger at all. A row-actions button that opens an
  // empty panel is worse than no button: it promises something and delivers a
  // blank rectangle.
  if (items.length === 0) return null;

  return (
    <>
      {trigger({
        ref: anchorRef,
        onClick: () => setOpen((value) => !value),
        'aria-expanded': open,
      })}
      <Popover
        open={open}
        onClose={() => setOpen(false)}
        anchorRef={anchorRef}
        align={align}
        label={label}
        className="min-w-44 py-1"
      >
        {header ? <div className="mb-1 border-b border-line px-3 pt-1.5 pb-2">{header}</div> : null}
        <div role="menu">
          {items.map((item, index) => (
            <div key={index}>
              {item.separated ? <div className="my-1 h-px bg-line" /> : null}
              <button
                type="button"
                role="menuitem"
                disabled={item.disabled}
                onClick={() => {
                  setOpen(false);
                  item.onSelect?.();
                }}
                className={cn(
                  'flex w-full items-center gap-2.5 px-3 py-1.5 text-left text-sm transition-colors',
                  'disabled:pointer-events-none disabled:opacity-45',
                  item.destructive
                    ? 'text-danger hover:bg-danger-subtle'
                    : 'text-body hover:bg-surface-2',
                )}
              >
                {item.icon ? <span className="shrink-0 text-faint">{item.icon}</span> : null}
                <span className="truncate">{item.label}</span>
              </button>
            </div>
          ))}
        </div>
      </Popover>
    </>
  );
}

// ── tooltip ─────────────────────────────────────────────────
/**
 * A hover hint.
 *
 * Shown on focus as well as hover, so it is reachable from the keyboard — a
 * hover-only tooltip is invisible to anybody not using a mouse, which on a
 * dense register is most of the icon-only controls.
 */
export function Tooltip({
  content,
  children,
  className,
}: {
  content: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLSpanElement>(null);

  if (!content) return <>{children}</>;

  return (
    <>
      <span
        ref={anchorRef}
        className={cn('inline-flex', className)}
        onPointerEnter={() => setOpen(true)}
        onPointerLeave={() => setOpen(false)}
        onFocusCapture={() => setOpen(true)}
        onBlurCapture={() => setOpen(false)}
      >
        {children}
      </span>
      <Popover
        open={open}
        onClose={() => setOpen(false)}
        anchorRef={anchorRef}
        align="center"
        className="pointer-events-none max-w-64 px-2.5 py-1.5"
      >
        <p className="text-2xs leading-relaxed text-body">{content}</p>
      </Popover>
    </>
  );
}

// ── confirm ─────────────────────────────────────────────────
export interface ConfirmProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
  title: string;
  /**
   * What will happen, in plain words.
   *
   * Required. "Are you sure?" asks a question the user cannot answer, because
   * it does not say what they are agreeing to.
   */
  message: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: ButtonVariant;
}

export function Confirm({
  open,
  onClose,
  onConfirm,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  variant = 'danger',
}: ConfirmProps) {
  const [busy, setBusy] = useState(false);

  const run = async () => {
    setBusy(true);
    try {
      await onConfirm();
      onClose();
    } finally {
      // Reset even on failure: the dialog stays open showing the error, and a
      // button stuck in its loading state would leave the user no way to retry.
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={busy ? () => undefined : onClose}
      title={title}
      size="sm"
      dismissOnBackdrop={!busy}
      footer={
        <>
          <Button variant="secondary" size="sm" onClick={onClose} disabled={busy}>
            {cancelLabel}
          </Button>
          <Button variant={variant} size="sm" onClick={run} loading={busy}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      <p className="text-sm leading-relaxed text-muted">{message}</p>
    </Modal>
  );
}

// ── wizard dialog ───────────────────────────────────────────
export interface WizardDialogProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  /** Beside the title, in a tonal tile. */
  icon?: ReactNode;
  steps: WizardStep[];
  /** The last step's button. */
  finishLabel: string;
  onFinish: () => void;
  /** The finish is under way. */
  busy?: boolean;
  /** Shown above the current step's fields — a refusal from the server, say. */
  error?: ReactNode;
  /** Under the rail: a summary of what is being made, as it fills in. */
  aside?: ReactNode;
  /** Every step may be opened from the start (an edit). */
  reachable?: boolean;
}

/**
 * A dialog that takes a long form one step at a time: the steps down the left
 * with where the person is, the step's own fields on the right, and the
 * footer saying what stops the next step. The dialog has a fixed height, so the
 * rail and the fields scroll inside themselves and the footer stays in view.
 * On a phone the rail folds to one line above the fields.
 */
export function WizardDialog({
  open,
  onClose,
  title,
  icon,
  steps,
  finishLabel,
  onFinish,
  busy = false,
  error,
  aside,
  reachable = false,
}: WizardDialogProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const wizard = useWizard(steps, open, reachable);

  useFocusTrap(panelRef, open);
  useScrollLock(open);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  if (!open) return null;
  const { current } = wizard;

  return (
    <Portal>
      <div className="fixed inset-0 z-overlay flex items-center justify-center p-4">
        <div className="absolute inset-0 animate-fade-in bg-black/40 backdrop-blur-[1px]" aria-hidden />
        <div
          ref={panelRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          tabIndex={-1}
          className="relative flex h-[min(90vh,46rem)] w-full max-w-5xl animate-scale-in flex-col overflow-hidden rounded-card bg-surface shadow-modal"
        >
          <div className="flex items-center justify-between gap-4 border-b border-line px-5 py-3.5">
            <div className="flex items-center gap-3">
              {icon ? (
                <span className="grid size-9 place-items-center rounded-control bg-accent-subtle text-accent [&_svg]:size-4.5">
                  {icon}
                </span>
              ) : null}
              <h2 id={titleId} className="text-base font-semibold text-heading">
                {title}
              </h2>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="-m-1 rounded p-1 text-faint transition-colors hover:bg-surface-2 hover:text-body"
              aria-label="Close"
            >
              <X className="size-4" />
            </button>
          </div>

          <div className="flex min-h-0 flex-1 flex-col md:flex-row">
            <ScrollArea className="hidden w-64 shrink-0 border-r border-line md:block" contentClassName="space-y-4 p-4">
              <div className="space-y-1.5">
                <div className="flex justify-between text-xs text-muted">
                  <span className="font-medium text-body">
                    Step {wizard.index + 1} of {steps.length}
                  </span>
                  <span>{wizard.percent}%</span>
                </div>
                <div className="h-1.5 rounded-full bg-surface-3">
                  <div
                    className="h-full rounded-full bg-accent transition-[width]"
                    style={{ width: `${wizard.percent}%` }}
                  />
                </div>
              </div>
              <ol className="space-y-1">
                {steps.map((step, index) => {
                  const here = index === wizard.index;
                  const done = index < wizard.index || (index <= wizard.furthest && !here);
                  const reach = wizard.canOpen(index);
                  return (
                    <li key={step.id}>
                      <button
                        type="button"
                        disabled={!reach}
                        aria-current={here ? 'step' : undefined}
                        onClick={() => wizard.open(index)}
                        className={cn(
                          'flex w-full items-start gap-3 rounded-control px-2.5 py-2 text-left transition-colors',
                          here ? 'bg-accent-subtle' : reach ? 'hover:bg-surface-2' : 'cursor-default opacity-60',
                        )}
                      >
                        <span
                          className={cn(
                            'grid size-7 shrink-0 place-items-center rounded-full border [&_svg]:size-3.5',
                            here
                              ? 'border-accent bg-accent text-accent-fg'
                              : done
                                ? 'border-accent text-accent'
                                : 'border-line-strong text-muted',
                          )}
                        >
                          {done && !here ? <Check /> : (step.icon ?? index + 1)}
                        </span>
                        <span className="min-w-0">
                          <span className={cn('block text-sm', here ? 'font-semibold text-heading' : 'text-body')}>
                            {step.title}
                          </span>
                          {step.hint ? <span className="block text-xs text-muted">{step.hint}</span> : null}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ol>
              {aside}
            </ScrollArea>

            <div className="flex items-center justify-between border-b border-line px-5 py-2 text-xs text-muted md:hidden">
              <span>
                Step {wizard.index + 1} of {steps.length}: <span className="font-medium text-body">{current.title}</span>
              </span>
              <span>{wizard.percent}%</span>
            </div>

            <ScrollArea key={current.id} className="min-h-0 flex-1" contentClassName="space-y-4 px-6 py-5">
              {current.heading ? (
                <div className="space-y-1">
                  <h3 className="text-lg font-semibold text-heading">{current.heading}</h3>
                  {current.description ? <p className="text-sm text-muted">{current.description}</p> : null}
                </div>
              ) : null}
              {error}
              {stepContent(current, wizard.openStep)}
            </ScrollArea>
          </div>

          <div className="flex flex-wrap items-center justify-end gap-2 border-t border-line bg-surface-2/50 px-5 py-3">
            {current.blocker ? <span className="mr-auto text-xs text-muted md:mr-2">{current.blocker}</span> : null}
            <Button variant="secondary" size="sm" onClick={onClose}>
              Cancel
            </Button>
            {wizard.isFirst ? null : (
              <Button variant="secondary" size="sm" onClick={wizard.back}>
                Back
              </Button>
            )}
            {wizard.isLast ? (
              <Button size="sm" loading={busy} disabled={Boolean(current.blocker)} onClick={onFinish}>
                {finishLabel}
              </Button>
            ) : (
              <Button size="sm" disabled={Boolean(current.blocker)} onClick={wizard.next}>
                Next
              </Button>
            )}
          </div>
        </div>
      </div>
    </Portal>
  );
}
